import PropTypes from 'prop-types';
import { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import EmailToClientForm from '../../EmailToClientForm';
import Modal from '../../Modal';
import CompareTable from './CompareTable';
import generateHtmlFromComponent from '../../../helpers/generateHtmlFromComponent';
import { sendEmailCompare, setFields } from '../../../store/slices/compare';
import getHotelPriceDisplayPreference from '../../../helpers/selectors/getHotelPriceDisplayPreference';
import { SearchStatus } from '../../../constants';
import ConfirmModal from '../../ConfirmModal';

const getInitFormObjectState = (email, name, emailHtml, travelAgency, compareSettings) => {
  const agentName = name || email.split('@')[0];
  const displayLabel = (compareSettings.DisplayName || 'compare').toLowerCase();

  const doHideCreatedFor = !!compareSettings.doHideCreatedFor;
  const doShowEmptyEmailMessage = !!compareSettings.doShowEmptyEmailMessage;

  const getDefaultEmailMessage = () => {
    if (doShowEmptyEmailMessage) return '';
    return travelAgency && travelAgency.defaultCompareText
      ? travelAgency.defaultCompareText.replace('{{agentName}}', agentName)
      : `Hello,
Here are a few options for you to ${displayLabel}.

Please let me know if you would like to proceed or have any questions.

Regards,
${agentName}`;
  };

  return ({
    createdFor: doHideCreatedFor ? undefined : '',
    fromEmail: email,
    emailRecipientsString: '',
    emailSubject: '',
    doSendCopyToAgent: true,
    doShowPricing: travelAgency ? travelAgency.defaultDoShowPricingToAnonymous : true,
    doEnableSave: true,
    doEnableSend: true,
    emailMessage: getDefaultEmailMessage(),
    emailHtml,
  });
};

const CompareEmailModal = ({ doShow, onClose }) => {
  const dispatch = useDispatch();
  const { email, name, travelAgency } = useSelector((state) => state.user);
  const hotelRateDisplayPreference = useSelector(getHotelPriceDisplayPreference());
  const {
    sendEmailCompareStatus,
    sendEmailCompareError,
    cruiseOptions,
    hotelOptions, flightOptions, carOptions, tourOptions,
  } = useSelector((state) => state.compare);
  const compareSettings = (travelAgency && travelAgency.settings
    && travelAgency.settings.compareSettings) || {};
  const doHideHeading = !!compareSettings.doHideHeading;
  const doHideSentCopyToMe = !!compareSettings.doHideSentCopyToMeCheckbox;
  const [formObject, setFormObject] = useState(getInitFormObjectState(
    email,
    name,
    undefined,
    travelAgency,
    compareSettings,
  ));

  const handleInputChange = (event) => {
    const fieldName = event.target.name;
    const fieldValue = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    const newObject = { ...formObject };
    newObject[fieldName] = fieldValue;
    setFormObject(newObject);
    dispatch(setFields(newObject));
  };
  const doShowHotelSourceInHotelName = !travelAgency || !travelAgency.settings
    || travelAgency.settings.doShowHotelSourceInHotelName;
  const [showConfirm, setShowConfirm] = useState(false);

  const hasIncompleteFlightSegments = useMemo(() => flightOptions.some(
    (itinerary) => itinerary.flightItinerary.isIncompleteItinerary,
  ), [flightOptions]);
  // Remove invalid flights from brochure
  const validFlightTrips = useMemo(() => flightOptions.filter(
    (itinerary) => !itinerary.flightItinerary.isIncompleteItinerary,
  ), [flightOptions]);

  const hasAnyTripSegments = (validFlightTrips && validFlightTrips.length)
  || (cruiseOptions && cruiseOptions.length) || (hotelOptions && hotelOptions
    .length) || (carOptions && carOptions.length) || (tourOptions && tourOptions.length);
  const sendCompare = () => {
    if (!hasAnyTripSegments) return;
    generateHtmlFromComponent(
      <CompareTable
        cruiseTravelOptions={cruiseOptions}
        hotelTravelOptions={hotelOptions}
        flightTravelOptions={validFlightTrips}
        tourTravelOptions={tourOptions}
        carTravelOptions={carOptions}
        doShowViewButtonColumn={false}
        doShowLabelWithPrice={false}
        doShowPricing={formObject.doShowPricing}
        hotelRateDisplayPreference={hotelRateDisplayPreference}
        doShowHotelSourceInHotelName={doShowHotelSourceInHotelName}
      />,
      (html) => {
        dispatch(sendEmailCompare({ compareTravelOptionsHtml: html }));
      },
    );
  };
  const handleSendClick = () => {
    if (hasIncompleteFlightSegments) {
      setShowConfirm(true);
      return;
    }
    sendCompare();
  };
  // TODO: add compare table (insert it as {children} into emailtoclientform?)

  return (
    <>
      <Modal
        open={doShow}
        onClose={onClose}
      >
        <div className="p-8">
          {doHideHeading ? <></>
            : (
              <div className="text-theme font-bold sm:text-sm xl:text-lg">
                Send via Email
              </div>
            )}
          <EmailToClientForm
            createdFor={formObject.createdFor}
            fromEmail={formObject.fromEmail}
            emailRecipientsString={formObject.emailRecipientsString}
            emailSubject={formObject.emailSubject}
            doSendCopyToAgent={formObject.doSendCopyToAgent}
            doShowPricing={formObject.doShowPricing}
            onInputChange={handleInputChange}
            doEnableSend={sendEmailCompareStatus !== SearchStatus.SEARCHING}
            onSendClick={handleSendClick}
            sendLabel="Send"
            emailMessage={formObject.emailMessage}
            sendStatus={sendEmailCompareStatus}
            sendError={sendEmailCompareError}
            doHideSentCopyToMe={doHideSentCopyToMe}
            doHideShowPricing={!!compareSettings.doHideShowPricing}
          >
            <CompareTable
              cruiseTravelOptions={cruiseOptions}
              hotelTravelOptions={hotelOptions}
              flightTravelOptions={flightOptions}
              carTravelOptions={carOptions}
              tourTravelOptions={tourOptions}
              doShowPricing={formObject.doShowPricing}
              hotelRateDisplayPreference={hotelRateDisplayPreference}
              doShowHotelSourceInHotelName={doShowHotelSourceInHotelName}
            />
          </EmailToClientForm>
          {showConfirm && (
          <ConfirmModal
            className="modal-safe-click-zone"
            open={showConfirm}
            onConfirm={() => {
              setShowConfirm(false);
              sendCompare();
            }}
            onCancel={() => setShowConfirm(false)}
            okButtonLabel="send"
            disableOkButton={!hasAnyTripSegments}
            message={!hasAnyTripSegments ? 'There are no valid trip segments to save. Please add the missing segments.' : (
              <div>
                <div className="mb-2">
                  <span role="img" aria-label="warning">⚠️</span>
                  {' '}
                  <b>Some flight options are incomplete.</b>
                </div>
                The return (inbound) segments are missing.
                These incomplete options will be removed before saving.
                Do you want to continue?
              </div>
            )}
          />
          )}

        </div>
      </Modal>
    </>
  );
};

CompareEmailModal.propTypes = {
  doShow: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
};

CompareEmailModal.defaultProps = {
  doShow: false,
};

export default CompareEmailModal;
